import faker from 'faker';
import PropTypes from 'prop-types';
import { noCase } from 'change-case';
import { useRef, useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useSelector, useDispatch } from "react-redux";
import { set, sub, formatDistanceToNow } from 'date-fns';
import { Icon } from '@iconify/react';
import bellFill from '@iconify/icons-eva/bell-fill';
import clockFill from '@iconify/icons-eva/clock-fill';
import doneAllFill from '@iconify/icons-eva/done-all-fill';
// material
import { alpha } from '@material-ui/core/styles';
import {
  Box,
  List,
  Badge,
  Button,
  Avatar,
  Tooltip,
  Divider,
  ListItem,
  Typography,
  ListItemText,
  ListSubheader,
  ListItemAvatar
} from '@material-ui/core';
// utils
import { mockImgAvatar } from '../../utils/mockImages';
// components
import Scrollbar from '../../components/Scrollbar';
import MenuPopover from '../../components/MenuPopover';
import { MIconButton } from '../../components/@material-extend';
import axios from 'axios';
import useAuth from '../../hooks/useAuth';
import { ajaxUrl, socket } from "../../config";
import { getNotifications, addNotification, updateNotification } from "../../redux/slices/notification";

// ----------------------------------------------------------------------

function renderContent(notification) {
  const title = (
    <Typography variant="subtitle2">
      {notification.title}
      <Typography component="span" variant="body2" sx={{ color: 'text.secondary' }}>
        &nbsp; {noCase(notification.description)}
      </Typography>
    </Typography>
  );

  // if (notification.type === 'film_uploaded') {
  //   return {
  //     avatar: <img alt={notification.title} src="/static/icons/ic_notification_package.svg" />,
  //     title
  //   };
  // }
  // if (notification.type === 'film_viewed') {
  //   return {
  //     avatar: <img alt={notification.title} src="/static/icons/ic_notification_shipping.svg" />,
  //     title
  //   };
  // }
  if (notification.type === 'mail') {
    return {
      avatar: <img alt={notification.title} src="/static/icons/ic_notification_mail.svg" />,
      title
    };
  }
  if (notification.type === 'chat_message') {
    return {
      avatar: <img alt={notification.title} src="/static/icons/ic_notification_chat.svg" />,
      title
    };
  }
  return {
    avatar: <img alt={notification.title} src={notification.avatar} />,
    title
  };
}

NotificationItem.propTypes = {
  notification: PropTypes.object.isRequired
};

function NotificationItem({ notification, handleClose }) {
  const dispatch = useDispatch();
  const { avatar, title } = renderContent(notification);

  const markNotificationRead = async (notificationId) => {
    await axios.post(`${ajaxUrl}/notification/updateNotification`, { notificationId: notificationId, userEmail: "" })
    dispatch(updateNotification(notificationId))
    handleClose()
  }

  return (
    <ListItem
      button
      onClick={_ => notification.isUnRead ? markNotificationRead(notification._id) : null}
      to={notification.type === "chat_message" ? "/dashboard/chat" : notification.type === "mail" ? "/dashboard/mail/all" : "/"}
      disableGutters
      component={RouterLink}
      sx={{
        py: 1.5,
        px: 2.5,
        mt: '1px',
        ...(notification.isUnRead && {
          bgcolor: 'action.selected'
        })
      }}
    >
      <ListItemAvatar>
        <Avatar sx={{ bgcolor: 'background.neutral' }}>{avatar}</Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={title}
        secondary={
          <Typography
            variant="caption"
            sx={{
              mt: 0.5,
              display: 'flex',
              alignItems: 'center',
              color: 'text.disabled'
            }}
          >
            <Box component={Icon} icon={clockFill} sx={{ mr: 0.5, width: 16, height: 16 }} />
            {formatDistanceToNow(new Date(notification.createdAt))}
          </Typography>
        }
      />
    </ListItem>
  );
}

export default function AdminNotificationsPopover() {
  const anchorRef = useRef(null);
  const { user } = useAuth();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const { notifications } = useSelector((state) => state.notification);
  const totalUnRead = notifications.filter((item) => item.isUnRead === true).length;

  useEffect(() => {
    dispatch(getNotifications(user.email))
  }, [dispatch, user.email])

  useEffect(() => {
    socket.on('notification', data => {
      console.log('admin notification', data)
      if (data.receiverEmails.includes(user.email)) {
        dispatch(addNotification(data))
      }
    });
  }, [])

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleMarkAllAsRead = async () => {
    await axios.post(`${ajaxUrl}/notification/updateNotification`, { notificationId: "all", userEmail: user.email });
    dispatch(updateNotification("all"))
    handleClose()
  };

  return (
    <>
      <MIconButton
        ref={anchorRef}
        onClick={handleOpen}
        color={open ? 'primary' : 'default'}
        sx={{
          ...(open && {
            bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.action.focusOpacity)
          })
        }}
      >
        <Badge badgeContent={totalUnRead} color="error">
          <Icon icon={bellFill} width={20} height={20} />
        </Badge>
      </MIconButton>

      <MenuPopover open={open} onClose={handleClose} anchorEl={anchorRef.current} sx={{ width: 360 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', py: 2, px: 2.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1">Notifications</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              You have {totalUnRead} unread messages
            </Typography>
          </Box>

          {totalUnRead > 0 && (
            <Tooltip title=" Mark all as read">
              <MIconButton color="primary" onClick={handleMarkAllAsRead}>
                <Icon icon={doneAllFill} width={20} height={20} />
              </MIconButton>
            </Tooltip>
          )}
        </Box>

        <Divider />

        <Scrollbar sx={{ height: { xs: 340, sm: 'auto' } }}>
          <List
            disablePadding
            subheader={
              <ListSubheader disableSticky sx={{ py: 1, px: 2.5, typography: 'overline' }}>
                New
              </ListSubheader>
            }
          >
            {notifications.filter(notification => notification.isUnRead).map((notification, index) => (
              <NotificationItem key={index} notification={notification} handleClose={handleClose} />
            ))}
            {!notifications.filter(notification => notification.isUnRead).length && <Typography component="span" variant="body1" sx={{ color: 'text.secondary', ml: 9 }}>No New Notifications</Typography>}
          </List>

          <List
            disablePadding
            subheader={
              <ListSubheader disableSticky sx={{ py: 1, px: 2.5, typography: 'overline' }}>
                Old
              </ListSubheader>
            }
          >
            {notifications.filter(notification => !notification.isUnRead).map((notification, index) => (
              <NotificationItem key={index} notification={notification} handleClose={handleClose} />
            ))}
            {!notifications.filter(notification => !notification.isUnRead).length && <Typography component="span" variant="body1" sx={{ color: 'text.secondary', ml: 9 }}>No Notifications</Typography>}
          </List>
        </Scrollbar>

        {/* <Divider />

        <Box sx={{ p: 1 }}>
          <Button fullWidth disableRipple component={RouterLink} to="#">
            View All
          </Button>
        </Box> */}
      </MenuPopover>
    </>
  );
}
